import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Turnstile } from "@/components/Turnstile";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Cloudflare Turnstile. Sem variável no env usa a chave de TESTE do
  // Cloudflare (sempre passa) — troque por VITE_TURNSTILE_SITE_KEY em produção.
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recaptchaToken) {
      toast.error("Aguarde a verificação de segurança para provar que você não é um robô.");
      return;
    }

    setLoading(true);

    try {
      const data = await authAPI.login({
        email,
        senha: password,
        recaptchaToken,
      });

      toast.success("Bem-vindo!");

      if (data.user?.must_change_password) {
        navigate("/alterar-senha");
        return;
      }

      // Redirecionar baseado no tipo
      if (data.user.tipo_usuario === "admin" || data.user.tipo_usuario === "professor") {
        navigate("/admin");
      } else {
        navigate("/aluno");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error instanceof Error ? error.message : "Email ou senha incorretos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#013079] to-[#1a4a9e] p-4">
      <Card className="w-full max-w-md shadow-xl animate-scale-in bg-white border border-gray-200">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto mb-4 flex justify-center">
            <img
              src="https://img.codigo1615.com.br/uploads/images/memoire/img_692461aa0c0ed3.98647780.png"
              alt="Memoire"
              className="h-28 w-auto object-contain"
              referrerPolicy="no-referrer"
            />
          </div>
          <CardTitle className="text-3xl font-bold text-[#013079]">Memoire</CardTitle>
          <CardDescription className="text-gray-600">Faça login para continuar</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Esconder a senha" : "Mostrar a senha"}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-center my-4">
              <Turnstile
                siteKey={siteKey}
                onVerify={(token) => setRecaptchaToken(token)}
                onExpire={() => setRecaptchaToken(null)}
                onError={() => setRecaptchaToken(null)}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#013079] hover:bg-[#012060] text-white font-medium transition-all shadow-md hover:shadow-lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">ou</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border-2 border-[#013079] text-[#013079] hover:bg-[#013079] hover:text-white font-medium transition-all"
              onClick={() => window.open('https://francesnativo.com.br/memoire', '_blank')}
            >
              🎓 Adquirir o Memoire
            </Button>

              <div className="text-sm text-center">
                <Link to="/forgot-password" className="inline-block py-2 font-medium text-[#013079] hover:text-[#012060]">
                  Esqueceu sua senha?
                </Link>
              </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
